/**
 * Fetches the public bubble config from the marketing AppSync API.
 *
 * Uses API key auth — the API key is public by design (it's in the
 * embed snippet on the tenant's site). The actual security boundary
 * is the domain allowlist enforced by the AppSync Lambda authorizer
 * on subsequent message mutations.
 */

import type { LoaderConfig, PublicBubbleConfig } from '../types/public-config';

const QUERY = `
  query GetBubbleConfig($tenantKey: ID!) {
    getBubbleConfigByTenantKey(tenantKey: $tenantKey) {
      tenantId
      enabled
      template
      position
      botName
      welcomeMessage
      primaryColor
      showPoweredBy
      identityMode
      appBundleUrl
      allowedDomains
      tenantType
    }
  }
`;

interface GraphQLResponse<T> {
  data?: T;
  errors?: Array<{ message: string }>;
}

export async function fetchBubbleConfig(
  loader: LoaderConfig,
): Promise<PublicBubbleConfig> {
  const resp = await fetch(loader.configApiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': loader.configApiKey,
    },
    body: JSON.stringify({
      query: QUERY,
      variables: { tenantKey: loader.tenantKey },
    }),
  });

  if (!resp.ok) {
    throw new Error(`config endpoint returned ${resp.status}`);
  }

  const body = (await resp.json()) as GraphQLResponse<{
    getBubbleConfigByTenantKey: PublicBubbleConfig | null;
  }>;

  if (body.errors && body.errors.length > 0) {
    throw new Error(body.errors[0].message);
  }

  const config = body.data?.getBubbleConfigByTenantKey;
  if (!config) {
    throw new Error('tenant key not found or bubble disabled');
  }

  return config;
}
